import { Card, useTheme } from '@mui/material';
import type { CardProps } from '@mui/material';
import type { ReactNode } from 'react';

export interface GlowingCardProps extends Omit<CardProps, 'children'> {
  children: ReactNode;
  glowColor?: 'primary' | 'secondary' | 'info';
  component?: React.ElementType;
  href?: string;
  target?: string;
}

export const GlowingCard = ({
  children,
  glowColor = 'primary',
  sx,
  ...props
}: GlowingCardProps) => {
  const theme = useTheme();

  const glowColors = {
    primary: theme.palette.mode === 'dark' ? 'rgba(0, 242, 234, 0.3)' : 'rgba(0, 212, 204, 0.3)',
    secondary: 'rgba(124, 58, 237, 0.3)',
    info: 'rgba(59, 130, 246, 0.3)',
  };

  return (
    <Card
      sx={{
        position: 'relative',
        overflow: 'hidden',
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: theme.palette.divider,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          borderColor: glowColor === 'primary' ? 'primary.main' : `${glowColor}.main`,
          transform: 'translateY(-4px)',
          boxShadow: `0 0 20px ${glowColors[glowColor]}`,
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(to bottom right, ${glowColors[glowColor]}, transparent)`,
          opacity: 0,
          transition: 'opacity 0.5s ease',
          pointerEvents: 'none',
        },
        '&:hover::before': {
          opacity: 0.1,
        },
        ...sx,
      }}
      {...props}
    >
      {children}
    </Card>
  );
};
